import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";

const FIELDS_TEXT = [
  { key: "address", label: "Address", placeholder: "Street, landmark, city", textarea: true },
  { key: "maps_url", label: "Google Maps link", placeholder: "https://maps.google.com/…" },
  { key: "wifi_name", label: "Wi-Fi network", placeholder: "StJames-5G" },
  { key: "wifi_password", label: "Wi-Fi password", placeholder: "the secret" },
  { key: "caretaker_name", label: "Caretaker name", placeholder: "e.g., Sundar" },
  { key: "caretaker_phone", label: "Caretaker phone", placeholder: "+91…" },
  { key: "keys_location", label: "Where the keys live", placeholder: "Under the brass urn by the back door", textarea: true },
];

function formatEditedAt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function HouseNotes({ session }) {
  const [notes, setNotes] = useState(null);
  const [draft, setDraft] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getNotes().then((d) => {
      setNotes(d);
      setDraft(d);
    });
  }, []);

  if (!notes || !draft) {
    return (
      <div className="font-body text-sm text-[#5C6B61]" data-testid="notes-loading">
        Opening the house log…
      </div>
    );
  }

  const set = (k, v) => setDraft({ ...draft, [k]: v });

  const setContact = (i, k, v) => {
    const next = [...(draft.contacts || [])];
    next[i] = { ...next[i], [k]: v };
    setDraft({ ...draft, contacts: next });
  };

  const addContact = () =>
    setDraft({
      ...draft,
      contacts: [...(draft.contacts || []), { label: "", phone: "" }],
    });

  const removeContact = (i) => {
    const next = [...(draft.contacts || [])];
    next.splice(i, 1);
    setDraft({ ...draft, contacts: next });
  };

  const cancel = () => {
    setDraft(notes);
    setEditing(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      const cleanContacts = (draft.contacts || []).filter(
        (c) => (c.label || "").trim() || (c.phone || "").trim()
      );
      const updated = await api.updateNotes({
        editor: session.name,
        address: draft.address || "",
        maps_url: draft.maps_url || "",
        wifi_name: draft.wifi_name || "",
        wifi_password: draft.wifi_password || "",
        caretaker_name: draft.caretaker_name || "",
        caretaker_phone: draft.caretaker_phone || "",
        keys_location: draft.keys_location || "",
        contacts: cleanContacts,
        house_rules: draft.house_rules || "",
      });
      setNotes(updated);
      setDraft(updated);
      setEditing(false);
      toast.success("House notes updated.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="grid grid-cols-1 lg:grid-cols-12 gap-8"
      data-testid="house-notes"
    >
      {/* Header strip */}
      <div className="lg:col-span-12 flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="tracker text-[10px] text-[#9E473F]">The House Log</p>
          <h2 className="font-display text-4xl text-[#1A2F24] mt-1">
            Everything you need before you go.
          </h2>
          {notes.last_edited_by && (
            <p className="font-body text-xs text-[#5C6B61] mt-2" data-testid="notes-edited-meta">
              last edited by{" "}
              <span className="text-[#1A2F24]">{notes.last_edited_by}</span> ·{" "}
              {formatEditedAt(notes.last_edited_at)}
            </p>
          )}
        </div>
        <div className="flex gap-3">
          {editing ? (
            <>
              <button
                onClick={cancel}
                disabled={saving}
                data-testid="notes-cancel-btn"
                className="px-5 py-3 font-body text-xs tracker text-[#1A2F24] border border-[#1A2F24] hover:bg-[#F3F4F1] transition-colors duration-300"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                data-testid="notes-save-btn"
                className="px-5 py-3 font-body text-xs tracker bg-[#1A2F24] text-[#FAF9F6] hover:bg-[#2C4A3A] transition-colors duration-300 disabled:opacity-60"
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
            </>
          ) : (
            <button
              onClick={() => setEditing(true)}
              data-testid="notes-edit-btn"
              className="px-5 py-3 font-body text-xs tracker bg-[#1A2F24] text-[#FAF9F6] hover:bg-[#2C4A3A] transition-colors duration-300"
            >
              Edit notes
            </button>
          )}
        </div>
      </div>

      {/* Left column: essentials */}
      <section className="lg:col-span-7 bg-white border border-[#DCE0D9] p-7 sm:p-9 space-y-7">
        <div>
          <p className="tracker text-[10px] text-[#5C6B61]">Essentials</p>
          <h3 className="font-display text-2xl text-[#1A2F24] mt-1">
            Where it is, how to get in
          </h3>
        </div>

        {FIELDS_TEXT.map((f) => (
          <Field
            key={f.key}
            field={f}
            value={editing ? draft[f.key] : notes[f.key]}
            onChange={(v) => set(f.key, v)}
            editing={editing}
          />
        ))}

        {!editing && notes.maps_url && (
          <a
            href={notes.maps_url}
            target="_blank"
            rel="noreferrer"
            data-testid="open-maps-link"
            className="inline-flex items-center gap-2 mt-2 font-body text-xs tracker text-[#9E473F] hover:text-[#1A2F24] transition-colors duration-300"
          >
            Open in Google Maps →
          </a>
        )}
      </section>

      {/* Right column: contacts + rules */}
      <aside className="lg:col-span-5 space-y-8">
        <section className="bg-white border border-[#DCE0D9] p-7" data-testid="contacts-section">
          <div className="flex items-center justify-between">
            <p className="tracker text-[10px] text-[#5C6B61]">Little Black Book</p>
            <span className="font-body text-xs text-[#1A2F24]">
              {(editing ? draft.contacts : notes.contacts || []).length}
            </span>
          </div>
          <h3 className="font-display text-2xl text-[#1A2F24] mt-1">
            Who to call
          </h3>

          <ul className="mt-5 divide-y divide-[#DCE0D9] border-t border-[#DCE0D9]">
            {(editing ? draft.contacts : notes.contacts || []).map((c, i) => (
              <li key={i} className="py-3" data-testid={`contact-row-${i}`}>
                {editing ? (
                  <div className="flex flex-wrap items-center gap-3">
                    <input
                      type="text"
                      value={c.label}
                      onChange={(e) => setContact(i, "label", e.target.value)}
                      placeholder="Electrician"
                      className="flex-1 min-w-[120px] bg-transparent border-b border-[#DCE0D9] focus:border-[#1A2F24] outline-none py-1 font-body text-sm text-[#1A2F24] transition-colors"
                    />
                    <input
                      type="text"
                      value={c.phone}
                      onChange={(e) => setContact(i, "phone", e.target.value)}
                      placeholder="+91…"
                      className="flex-1 min-w-[140px] bg-transparent border-b border-[#DCE0D9] focus:border-[#1A2F24] outline-none py-1 font-body text-sm text-[#1A2F24] transition-colors"
                    />
                    <button
                      onClick={() => removeContact(i)}
                      data-testid={`remove-contact-${i}`}
                      className="font-body text-[10px] tracker text-[#9E473F] hover:text-[#1A2F24] transition-colors"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="font-body text-sm text-[#1A2F24]">
                      {c.label || "—"}
                    </span>
                    <a
                      href={`tel:${c.phone}`}
                      className="font-display text-lg text-[#1A2F24] hover:text-[#9E473F] transition-colors"
                    >
                      {c.phone}
                    </a>
                  </div>
                )}
              </li>
            ))}
            {(editing ? draft.contacts : notes.contacts || []).length === 0 && (
              <li className="py-4 font-body text-sm text-[#5C6B61]">
                No numbers yet — add the caretaker, electrician, doctor, gas vendor…
              </li>
            )}
          </ul>

          {editing && (
            <button
              onClick={addContact}
              data-testid="add-contact-btn"
              className="mt-5 font-body text-xs tracker text-[#9E473F] hover:text-[#1A2F24] transition-colors"
            >
              + add a number
            </button>
          )}
        </section>

        <section className="bg-white border border-[#DCE0D9] p-7" data-testid="rules-section">
          <p className="tracker text-[10px] text-[#5C6B61]">Before you leave</p>
          <h3 className="font-display text-2xl text-[#1A2F24] mt-1">House rules</h3>

          {editing ? (
            <textarea
              value={draft.house_rules || ""}
              onChange={(e) => set("house_rules", e.target.value)}
              rows={9}
              placeholder={"- Turn off the geyser\n- Lock the back gate\n- Empty the kitchen bin\n- Leave a note in the log"}
              data-testid="rules-textarea"
              className="mt-4 w-full bg-transparent border border-[#DCE0D9] focus:border-[#1A2F24] outline-none p-3 font-body text-sm text-[#1A2F24] resize-none transition-colors duration-300"
            />
          ) : notes.house_rules ? (
            <p className="mt-4 font-body text-sm text-[#1A2F24] whitespace-pre-line leading-relaxed">
              {notes.house_rules}
            </p>
          ) : (
            <p className="mt-4 font-body text-sm text-[#5C6B61]">
              No rules written yet. Click <em>Edit notes</em> to add a leaving checklist.
            </p>
          )}
        </section>
      </aside>
    </motion.div>
  );
}

function Field({ field, value, onChange, editing }) {
  const display = value && value.trim().length > 0 ? value : null;
  if (editing) {
    return (
      <div data-testid={`field-${field.key}`}>
        <label className="tracker text-[10px] text-[#5C6B61]">
          {field.label}
        </label>
        {field.textarea ? (
          <textarea
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            rows={2}
            className="mt-2 w-full bg-transparent border border-[#DCE0D9] focus:border-[#1A2F24] outline-none p-3 font-body text-sm text-[#1A2F24] resize-none transition-colors duration-300"
          />
        ) : (
          <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            className="mt-2 w-full bg-transparent border-b border-[#DCE0D9] focus:border-[#1A2F24] outline-none py-2 font-body text-base text-[#1A2F24] transition-colors duration-300"
          />
        )}
      </div>
    );
  }
  return (
    <div data-testid={`field-${field.key}`}>
      <p className="tracker text-[10px] text-[#5C6B61]">{field.label}</p>
      {display ? (
        <p className="mt-1 font-body text-base text-[#1A2F24] whitespace-pre-line">
          {display}
        </p>
      ) : (
        <p className="mt-1 font-body text-sm italic text-[#5C6B61]">
          not set yet
        </p>
      )}
    </div>
  );
}
